import { createSelector } from '@reduxjs/toolkit';
import {
  ALI_GEOCOMM,
  ALI_INDIGITAL,
} from '@rsos/capstone/src/constants/capabilities';
import {
  PREMIUM_ALI_GEOCOMM,
  PREMIUM_ALI_INDIGITAL,
} from '@rsos/constants/permissions';
import { EmergencyCall } from '@rsos/reduxDataStore/src/features/emergencyCalls/emergencyCalls.types';
import {
  filterAniAliCalls,
  filterEmergencyCalls,
} from '@rsos/reduxDataStore/src/utils/filterEmergencyCalls';
import { RootState } from '../app';
import {
  sortCallQueueByCallStartTime,
  type TextFrom911,
} from '../utils/sortCallQueueByCallStartTime';

// TODO: this should be in a declaration (.d.ts) file
type DynamicObject = {
  [key: string]: Record<string, unknown>;
};
type ConversationDataObject = {
  [key: string]: unknown;

  data: DynamicObject & {
    isOutboundSMS?: boolean;
  };
  loading: DynamicObject;
  error: DynamicObject;
};

interface CurrentPsaps {
  // TODO: replace any with a better type
  activeCapabilities: any;
}

/**
 * Memoized selector that derives the call queue data based on whether or not the user has access to
 * ANI/ALI features. Only recalculates when the relevant permissions/capabilities or call queue changes.
 * This matches callQueueSelector() in reduxDataStore/.../callQueueSelector.js
 */
// TODO: create another selector just for this for non Queue related features
// that need the call queue
export const selectCallQueueBasedOnAniAliAccess = createSelector(
  [
    (state: RootState) =>
      (state.psaps?.currentPSAP as CurrentPsaps)?.activeCapabilities?.[
        ALI_GEOCOMM
      ],
    (state: RootState) =>
      state.sinatra?.user?.currentRole?.permissions?.includes(
        PREMIUM_ALI_GEOCOMM,
      ),
    (state: RootState) =>
      (state.psaps?.currentPSAP as CurrentPsaps)?.activeCapabilities?.[
        ALI_INDIGITAL
      ],
    (state: RootState) =>
      state.sinatra?.user?.currentRole?.permissions?.includes(
        PREMIUM_ALI_INDIGITAL,
      ),
    (state: RootState) => state.emergencyCalls.callQueue,
  ],
  (
    aliGeocommCapability,
    hasGeocommPermission,
    aliIndigitalCapability,
    hasIndigitalPermission,
    callQueue,
  ) => {
    const hasAliGeocommAccess = !!(
      aliGeocommCapability && hasGeocommPermission
    );
    const hasAliIndigitalAccess = !!(
      aliIndigitalCapability && hasIndigitalPermission
    );
    const hasAniAliAccess = hasAliGeocommAccess || hasAliIndigitalAccess;

    if (hasAniAliAccess) {
      return filterEmergencyCalls(callQueue);
    }

    return filterAniAliCalls(filterEmergencyCalls(callQueue));
  },
);

const selectOutboundTextFrom911Conversations = createSelector(
  [(state: RootState) => state?.textFrom911?.conversations],
  conversations => {
    if (!conversations) return {};

    const typedConversations = conversations as {
      [key: string]: ConversationDataObject;
    };

    return Object.keys(typedConversations).reduce((acc, key) => {
      const conversationData = typedConversations[key];
      acc[key] = conversationData;
      return acc;
    }, {} as DynamicObject);
  },
);

const callQueueSelectorNext = createSelector(
  selectCallQueueBasedOnAniAliAccess,
  selectOutboundTextFrom911Conversations,
  (
    filteredCallQueue: Record<string, EmergencyCall>,
    // TODO: replace any with a better type
    textFrom911Conversations: { [key: string]: any },
  ) => {
    const callsToRender: Record<string, EmergencyCall | TextFrom911> = {
      ...filteredCallQueue,
    };

    for (const phoneNumber in textFrom911Conversations) {
      // If there is phone number has a text from 911 conversation that doesn't
      // exist in the call queue, it should render
      if (!filteredCallQueue?.[phoneNumber]) {
        callsToRender[phoneNumber] = {
          textFrom911: { ...textFrom911Conversations[phoneNumber] },
        };
      } else {
        callsToRender[phoneNumber] = {
          ...filteredCallQueue[phoneNumber],
          textFrom911: { ...textFrom911Conversations[phoneNumber] },
          // TODO: the structure should be converted to this for better organization
          // {
          //   callQueue: { ... },
          //   textFrom911: { ... },
          // },
        };
      }
    }
    return sortCallQueueByCallStartTime(callsToRender);
  },
);

export default callQueueSelectorNext;
